'use client';

import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

export default function DetailsTab({ project, onUpdateSuccess }) {
  const navigate = useNavigate();
  
  // Initialize the form with the project data.
  const [formData, setFormData] = useState({
    id: project.id,
    name: project.name || null,
    client_name: project.client_name || null,
    address: project.address || null,
    contact_name: project.contact_name || null,
    contact_phone: project.contact_phone || null,
    contact_email: project.contact_email || null,
    start_date: project.start_date || null,
    end_date: project.end_date || null,
    description: project.description || null,
    active: project.active !== undefined ? project.active : true, // Default to true if undefined
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Handle changes for each input field.
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Update the project record in the projects table.
  const handleUpdate = async () => {
    setLoading(true);
    setError('');
    try {
      const { error } = await supabase
        .from('projects')
        .update({
          name: formData.name,
          client_name: formData.client_name,
          address: formData.address,
          contact_name: formData.contact_name,
          contact_phone: formData.contact_phone,
          contact_email: formData.contact_email,
          start_date: formData.start_date,
          end_date: formData.end_date,
          description: formData.description,
          active: formData.active, // Add active field
        })
        .eq('id', project.id);
      if (error) {
        setError(error.message);
      } else {
        // Call onUpdateSuccess only if it's a function
        if (typeof onUpdateSuccess === 'function') {
          onUpdateSuccess();
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setEditMode(false);
    }
  };

  // Delete the project from the database
  const handleDelete = async () => {
    setLoading(true);
    setError('');
    try {
      // Delete the project from the projects table
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', project.id);
      
      if (error) {
        setError(error.message);
      } else {
        // Navigate back to projects list
        navigate('/projects');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while deleting the project');
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg border">
      {/* Section Header */}
      <div className="pb-5 flex justify-between items-center">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Project Details</h2>
          <p className="mt-2 max-w-2xl text-sm text-gray-500">
            Review the below project information and edit if necessary.
          </p>
        </div>
        
        <div className="ml-4 mt-4 flex shrink-0 gap-x-4">
          <div>
          <button
            type="button"
            className="relative ml-3 inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            onClick={() => {
              if (!editMode) {
                setEditMode(true);
              } else {
                handleUpdate();
              }
            }}
            disabled={loading}
          >
            {!editMode ? (
              <>
              <PencilIcon className="w-5 h-5 mr-2" />
                Edit
              </>
            ) : (
              'Save'
            )}
          </button>
          </div>
        </div>
      </div>

      {/* Editable Form */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-center">
          <label htmlFor="active" className="text-sm font-medium text-gray-700 sm:w-1/3">
            Status
          </label>
          <select
            id="active"
            name="active"
            value={formData.active.toString()}
            onChange={(e) => handleChange({ target: { name: 'active', value: e.target.value === 'true' } })}
            disabled={!editMode}
            className="mt-1 sm:mt-0 sm:w-2/3 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm disabled:opacity-60 disabled:bg-gray-200"
          >
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
        <div className="flex flex-col sm:flex-row justify-between items-center">
          <label htmlFor="name" className="text-sm font-medium text-gray-700 sm:w-1/3">
            Project Name
          </label>
          <input
            id="name"
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            disabled={!editMode}
            className="mt-1 sm:mt-0 sm:w-2/3 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm disabled:opacity-60 disabled:bg-gray-200"
          />
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-center">
          <label htmlFor="client_name" className="text-sm font-medium text-gray-700 sm:w-1/3">
            Client Name
          </label>
          <input
            id="client_name"
            type="text"
            name="client_name"
            value={formData.client_name}
            disabled={!editMode}
            onChange={handleChange}
            className="mt-1 sm:mt-0 sm:w-2/3 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm disabled:opacity-60 disabled:bg-gray-200"
          />
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-center">
          <label htmlFor="address" className="text-sm font-medium text-gray-700 sm:w-1/3">
            Project Address
          </label>
          <input
            id="address"
            type="text"
            name="address"
            value={formData.address}
            onChange={handleChange}
            disabled={!editMode}
            className="mt-1 sm:mt-0 sm:w-2/3 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm disabled:opacity-60 disabled:bg-gray-200"
          />
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-center">
          <label htmlFor="contact_name" className="text-sm font-medium text-gray-700 sm:w-1/3">
            Contact Name
          </label>
          <input
            id="contact_name"
            type="text"
            name="contact_name"
            value={formData.contact_name}
            onChange={handleChange}
            disabled={!editMode}
            className="mt-1 sm:mt-0 sm:w-2/3 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm disabled:opacity-60 disabled:bg-gray-200"
          />
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-center">
          <label htmlFor="contact_phone" className="text-sm font-medium text-gray-700 sm:w-1/3">
            Contact Phone
          </label>
          <input
            id="contact_phone"
            type="text"
            name="contact_phone"
            value={formData.contact_phone}
            onChange={handleChange}
            disabled={!editMode}
            className="mt-1 sm:mt-0 sm:w-2/3 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm disabled:opacity-60 disabled:bg-gray-200"
          />
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-center">
          <label htmlFor="contact_email" className="text-sm font-medium text-gray-700 sm:w-1/3">
            Contact Email
          </label>
          <input
            id="contact_email"
            type="email"
            name="contact_email"
            value={formData.contact_email}
            onChange={handleChange}
            disabled={!editMode}
            className="mt-1 sm:mt-0 sm:w-2/3 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm disabled:opacity-60 disabled:bg-gray-200"
          />
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-center">
          <label htmlFor="start_date" className="text-sm font-medium text-gray-700 sm:w-1/3">
            Start Date
          </label>
          <input
            id="start_date"
            type="date"
            name="start_date"
            value={formData.start_date ? formData.start_date.split('T')[0] : ''}
            onChange={handleChange}
            disabled={!editMode}
            className="mt-1 sm:mt-0 sm:w-2/3 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm disabled:opacity-60 disabled:bg-gray-200"
          />
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-center">
          <label htmlFor="end_date" className="text-sm font-medium text-gray-700 sm:w-1/3">
            End Date
          </label>
          <input
            id="end_date"
            type="date"
            name="end_date"
            value={formData.end_date ? formData.end_date.split('T')[0] : ''}
            onChange={handleChange}
            disabled={!editMode}
            className="mt-1 sm:mt-0 sm:w-2/3 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm disabled:opacity-60 disabled:bg-gray-200"
          />
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-center">
          <label htmlFor="description" className="text-sm font-medium text-gray-700 sm:w-1/3">
            Project Description
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            disabled={!editMode}
            rows={3}
            className="mt-1 sm:mt-0 sm:w-2/3 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm disabled:opacity-60 disabled:bg-gray-200"
          ></textarea>
        </div>

        {error && <p className="text-red-500 text-sm">{error}</p>}
      </div>

      {/* Delete Project Button */}
      <div className="mt-8 border-t pt-6">
        <div className="flex flex-col sm:flex-row justify-between items-center">
          <div className="sm:w-1/3">
            <h3 className="text-sm font-medium text-gray-700">Danger Zone</h3>
            <p className="text-sm text-gray-500">Once deleted, this project cannot be recovered.</p>
          </div>
          <div className="mt-4 sm:mt-0 sm:w-2/3 flex justify-end">
            <button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              disabled={loading}
            >
              <TrashIcon className="w-5 h-5 mr-2" />
              Delete Project
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <TrashIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      Delete Project
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete this project? This action cannot be undone.
                        All measurements will be removed from the project and appear in the unmapped page.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleDelete}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  disabled={loading}
                >
                  {loading ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}